import * as React from "react";
import { cn } from "./cn";
import { useReducedMotion } from "../hooks/useReducedMotion";

/**
 * BorderBeam — animated conic-gradient border. The beam runs around the
 * inside of a parent's border using a CSS mask so it shows up as a
 * single bright arc rotating clockwise.
 *
 * Why vendored, not a dep:
 *   The whole pattern is one mask + one keyframe. Adding `@magicui/*`
 *   for it would pull in unrelated components and bump our bundle. The
 *   wider visual ecosystem this style comes from (Aceternity, Magic UI)
 *   is open-source MIT — we built our own variant tuned to our tokens.
 *
 * Use through Badge's `shimmer` prop. Don't reach for it directly
 * unless you're sure — it's expressive and earns its place only on
 * "earned attention" surfaces (premium / new / accent).
 *
 * Reduce-motion: renders a static border, no rotation.
 */

export interface BorderBeamProps extends React.HTMLAttributes<HTMLSpanElement> {
  /**
   * Beam width in px. Default 2. Use an integer — fractional values
   * (e.g. 1.5) subpixel-round inconsistently across edges on small
   * elements (Badge), causing visibly uneven ring thickness.
   */
  size?: number;
  /** Animation duration in seconds. Default 4. */
  duration?: number;
  /** CSS color for the bright edge. Defaults to `hsl(var(--accent))`. */
  color?: string;
  /** Border radius — should match the wrapped element's radius. */
  radius?: string;
  /** Optional class on the inner content wrapper. */
  innerClassName?: string;
  children: React.ReactNode;
}

/**
 * Wraps `children` in a relative container, draws the rotating beam in
 * the ::before pseudo-equivalent (a positioned span), and clips it
 * with a CSS mask so only the border shows. Inline styles let
 * consumers tune `size` / `duration` / `color` per call without
 * defining new keyframes.
 */
export const BorderBeam = React.forwardRef<HTMLSpanElement, BorderBeamProps>(
  (
    {
      size = 2,
      duration = 4,
      color = "hsl(var(--accent))",
      radius = "9999px",
      className,
      innerClassName,
      children,
      style,
      ...props
    },
    ref,
  ) => {
    const reduced = useReducedMotion();

    return (
      <span
        ref={ref}
        className={cn("relative inline-block", className)}
        style={{ borderRadius: radius, ...style }}
        {...props}
      >
        {/* Beam layer — a conic gradient that rotates. The mask hides
            the inside of the gradient so only the perimeter glows.
            `box-sizing: border-box` is set explicitly so padding stays
            inside `inset: 0` regardless of consumer CSS resets — without
            it, a content-box default makes padding extend outward and
            produces uneven ring thickness on the visible perimeter. */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            borderRadius: radius,
            boxSizing: "border-box",
            padding: `${size}px`,
            background: `conic-gradient(from var(--beam-angle, 0deg), transparent 0deg, ${color} 35deg, transparent 70deg, transparent 360deg)`,
            // Mask lets only the outer ring of the gradient through —
            // produces the "border beam" effect.
            WebkitMask:
              "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
            mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
            animation: reduced
              ? undefined
              : `aisquare-beam-rotate ${duration}s linear infinite`,
          }}
        />
        {/* The content sits above the beam layer. */}
        <span
          className={cn("relative inline-block", innerClassName)}
          style={{ borderRadius: radius }}
        >
          {children}
        </span>
        {/* Keyframes scoped via a one-shot <style>. The `--beam-angle`
            CSS var lets the animation drive `from <angle>` in the
            conic-gradient — works in modern browsers via @property,
            and degrades to a static beam in older ones. */}
        <style>{`@keyframes aisquare-beam-rotate {
          0% { --beam-angle: 0deg; }
          100% { --beam-angle: 360deg; }
        }
        @property --beam-angle {
          syntax: "<angle>";
          inherits: false;
          initial-value: 0deg;
        }`}</style>
      </span>
    );
  },
);
BorderBeam.displayName = "BorderBeam";
